import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Trash2, MailPlus, RotateCcw, X } from "lucide-react";

const ALL_ROLES = ["admin", "designer", "videographer", "photographer", "member", "agent"];

const ROLE_TINT = {
  admin: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  designer: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  videographer: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  photographer: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  member: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  agent: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

export default function Users() {
  const { t } = useLang();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("OK");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("users")}</h1>
        <p className="text-slate-400 mt-1 text-sm">{users.length}</p>
      </div>
      <div className="glass rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-slate-400">{t("name")}</TableHead>
              <TableHead className="text-slate-400">{t("email")}</TableHead>
              <TableHead className="text-slate-400">{t("phone")}</TableHead>
              <TableHead className="text-slate-400">{t("role")}</TableHead>
              <TableHead className="text-slate-400 text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="border-white/5 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-slate-300">{u.email}</TableCell>
                <TableCell>
                  {isAdmin ? (
                    <PhoneCell user={u} onSaved={load} t={t} />
                  ) : (
                    <span className="text-slate-300 text-sm">{u.phone || "—"}</span>
                  )}
                </TableCell>
                <TableCell>
                  {isAdmin && u.id !== user.id ? (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger data-testid={`user-role-select-${u.id}`} className="w-[160px] bg-white/5 border-white/10 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALL_ROLES.map(r => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={ROLE_TINT[u.role] || ROLE_TINT.member}>{t(u.role)}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {isAdmin && u.id !== user.id && (
                    <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {isAdmin && <InvitePanel t={t} />}
    </div>
  );
}

function PhoneCell({ user, onSaved, t }) {
  const [value, setValue] = useState(user.phone || "");
  const [busy, setBusy] = useState(false);
  const dirty = value !== (user.phone || "");

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { phone: value });
      toast.success(t("phone_saved"));
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1.5">
      <Input data-testid={`user-phone-input-${user.id}`} value={value} placeholder="+243990000000"
        onChange={(e) => setValue(e.target.value)} className="w-[160px] h-8 bg-white/5 border-white/10 text-sm" />
      {dirty && (
        <Button size="sm" data-testid={`user-phone-save-${user.id}`} onClick={save} disabled={busy} className="h-8 bg-indigo-500 hover:bg-indigo-600">
          {busy ? "…" : t("save")}
        </Button>
      )}
    </div>
  );
}

function InvitePanel({ t }) {
  const [form, setForm] = useState({ email: "", name: "", role: "designer" });
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/invitations").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invitations", form);
      toast.success(t("invite_sent"));
      if (data.invite_url) navigator.clipboard?.writeText(data.invite_url).catch(() => {});
      setForm({ email: "", name: "", role: "designer" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resend = async (id) => {
    try { await api.post(`/invitations/${id}/resend`); toast.success(t("invite_resent")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/invitations/${id}`); toast.success(t("invite_revoked")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="glass rounded-2xl p-6 space-y-5" data-testid="invite-panel">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <MailPlus className="w-5 h-5 text-indigo-300" />{t("invite_collaborator")}
        </h2>
      </div>
      <form onSubmit={send} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-xs text-slate-400">{t("name")}</Label>
          <Input data-testid="invite-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("email")}</Label>
          <Input data-testid="invite-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-white/5 border-white/10" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">{t("role")}</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="invite-role-select" className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>{ALL_ROLES.map((r) => <SelectItem key={r} value={r}>{t(r)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button type="submit" data-testid="invite-send-btn" disabled={busy} className="btn-glow bg-indigo-500 hover:bg-indigo-600">{busy ? "…" : t("invite_send")}</Button>
      </form>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">{t("invite_pending")} · {invites.length}</div>
        {invites.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">{t("no_data")}</div>
        ) : (
          <div className="divide-y divide-white/5">
            {invites.map((i) => (
              <div key={i.id} data-testid={`invite-row-${i.id}`} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium truncate">{i.name} <span className="text-xs text-slate-400">· {i.email}</span></div>
                  <div className="text-xs text-slate-400">{t("invite_expires")} {i.expires_at.slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={ROLE_TINT[i.role] || ROLE_TINT.member}>{t(i.role)}</Badge>
                  <Button size="sm" variant="ghost" data-testid={`invite-resend-${i.id}`} onClick={() => resend(i.id)} className="text-indigo-300 hover:bg-indigo-500/10">
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`invite-revoke-${i.id}`} onClick={() => revoke(i.id)} className="text-rose-400 hover:bg-rose-500/10">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
